import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Component, computed, effect, HostListener, inject, Inject, Input, OnInit, PLATFORM_ID, Renderer2 } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { InputText } from 'primeng/inputtext';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SelectModule } from 'primeng/select';
import { SelectButtonModule } from 'primeng/selectbutton';
import { TextareaModule } from 'primeng/textarea';
import { distinctUntilChanged, Subscription } from 'rxjs';
import { IDonor } from '../../../models/IDonor';
import { ILineItems } from '../../../models/ILineItems';
import { ApiService } from '../../../services/api.service';
import { AuthService, LOGIN_TYPE_TICKETS } from '../../../services/authentication/auth.service';
import { CashDonationService } from '../../../services/donations/cash-donation.service';
import { EmbeddedPaymentService } from '../../../services/epf/embedded-payment.service';
import { ThemeService } from '../../../services/theme.service';
import { ToastMessageService } from '../../../services/toast-message/toast-message.service';
import { SanitizeHtmlTextPipe } from '../../../shared/pipes/sanitize-html-text.pipe';
import { EmbeddedPaymentComponent } from '../../embedded-payment/embedded-payment.component';
import { FooterComponent } from '../footer/footer.component';
import { HeaderComponent } from '../header/header.component';

@Component({
  selector: 'app-donation',
  imports: [FormsModule,
    ReactiveFormsModule,
    SelectButtonModule,
    InputText,
    CheckboxModule,
    SelectModule,
    TextareaModule,
    ButtonModule,
    CommonModule,
    HeaderComponent,
    FooterComponent,
    EmbeddedPaymentComponent,
    RouterModule,
    SanitizeHtmlTextPipe,
    ProgressSpinnerModule],
  templateUrl: './donation.component.html',
  styleUrl: './donation.component.scss'
})
export class DonationComponent implements OnInit {

  private readonly paymentService = inject(EmbeddedPaymentService);
  private readonly donationService = inject(CashDonationService);
  private readonly oidcSecurityService = inject(OidcSecurityService);
  private readonly apiService = inject(ApiService);
  private readonly messageService = inject(ToastMessageService);
  private readonly themeService = inject(ThemeService);
  private readonly router = inject(Router);


  homePageSignal = this.apiService.homePageSignal;
  // auth
  authService = inject(AuthService);
  isAuthenticated = this.oidcSecurityService.authenticated;


  cashDonationConfigSignal = this.donationService.cashDonationConfigSignal;
  cashDonationText: string | null = null;


  // config = this.donationService.cashDonationConfigSignal();

  @Input() id!: number;
  @Input() donors: IDonor[] = [];

  otherAmount: number | null = null;
  coverFee: boolean = false;
  comment: string | null = null;
  totalAmount: number = 0;
  coverFeeRate: number = 0.08;
  isAnonymous: boolean = false;
  amounts!: number[];
  isValidAmount: boolean = true;
  showDonorNameAlert: boolean = false;

  authSubscription!: Subscription;


  isMobile: boolean = false;
  mobileStep = 1;

  token: string = '';
  visibleEmbedded: boolean = false;

  auctionConfigSignal = this.apiService.auctionConfigSignal;

  protected showWinningBidText = computed(() => {
    return this.apiService.auctionConfigSignal()?.processor === 'pgf'
  });

  protected currency = computed(() => this.apiService.auctionConfigSignal()?.currency ?? '')

  constructor(@Inject(PLATFORM_ID) private platformId: Object,
    private renderer: Renderer2) {

    effect(() => {
      const data = this.donationService.cashDonationConfigSignal();
      if (data) {
        console.log(data);
        this.amounts = data.donationAmounts;
        // console.log(this.amounts.toString())
        this.selectedAmount = this.amounts[0];
        this.totalAmount = this.selectedAmount;
        this.cashDonationText = data.cashDonationText;
      }
    });
  }

  private unlistenMessage?: () => void; // for cleanup

  selectedAmount: number | null = null;

  // creditCards = [
  //   { label: 'Visa 0250', id: 1 },
  //   { label: 'MasterCard 5678', id: 2 },
  //   { label: 'American Express 9876', id: 3 }
  // ];
  selectedCard: string = '';
  selectedDonor: string | null = null;
  donorInputValue: string = '';

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.themeService.customizeAuctionTheme(this.id);
    }
    if (typeof window !== 'undefined') {
      this.updateIsMobile();
      if (!this.apiService.homePageSignal()) {
        const endpoint = 'auctions';
        this.apiService.getAuctionHomePageData(endpoint, this.id!);
        localStorage.setItem('homePageData', JSON.stringify(this.apiService.homePageSignal()));
      }

      this.authSubscription = this.authService.isAuthenticated$.pipe(distinctUntilChanged()).subscribe(isAuth => {
        if (isAuth) {
          this.oidcSecurityService.getUserData().subscribe(result => {
            if (result) {
              if (!this.isAnonymous) {
                this.donorInputValue = result.name ?? '';
                this.selectedDonor = this.donorInputValue;
              }
            }
          });
        } else {
          this.donorInputValue = '';
          this.selectedDonor = '';
        }
      });
    }

    // Add window message listener for Unavailable Notification support
    this.unlistenMessage = this.renderer.listen('window', 'message', this.onHandleEpfMessage);

    this.getCashDonationConfig();

    if (!this.auctionConfigSignal()) {
      this.apiService.getAuctionConfig(this.id ?? 0).subscribe();
    }
  }


  ngOnDestroy() {
    this.authSubscription?.unsubscribe();
    // clean up the window listener if it was registered
    if (this.unlistenMessage) {
      this.unlistenMessage();
      this.unlistenMessage = undefined;
    }
  }

  onHandleEpfMessage = (event: any) => {
    console.log(event.data);

    if (event.data !== null) {
      if (event.data.type === "ITEM_UNAVAILABLE") {
        this.visibleEmbedded = false; // this closes modal
        // If we want to route somewhere else, example:
        // this.route.navigate(['/itemunavailable']);
        window.location.reload();
        }  else if (event.data.type === "EPF_REDIRECT_PARENT_WITH_URL" && event.data.url) {
                    console.log('Redirect to Thank You page');
                    this.visibleEmbedded = false; // this closes modal
                    window.location.replace(event.data.url);
        }

    }
  };

  getCashDonationConfig() {
    if (typeof window !== 'undefined') {
      // console.log('CashDonation Executed')
      this.donationService.getDonationConfig(this.id).subscribe();
    }
  }

  openEmbeddedPayment(token: string) {
    this.token = token;
    this.visibleEmbedded = true;
  }

  onValidateAmount(event: Event) {
    const value = (event.target as HTMLInputElement).value.trim();

    if (value === '') {
      this.selectAmount(this.amounts[0]);
      this.isValidAmount = true;
      return;
    }
    this.isValidAmount = /^\d+$/.test(value);
  }

  onNextStepMobile() {
    if (!this.isAnonymous && this.donorInputValue.length === 0) {
      this.showDonorNameAlert = true;
      return;
    } else {
      this.mobileStep = 2;
    }
  }


  @HostListener('window:resize')
  onResize() {
    this.updateIsMobile();
  }

  private updateIsMobile() {
    // guard window usage
    if (isPlatformBrowser(this.platformId)) {
      this.isMobile = window.innerWidth < 1024;
      // console.log('isMobile:', this.isMobile);

      if (!this.isMobile) {
        this.mobileStep = 1;
      }
    }
  }

  updateTotalAmount() {

    const other = Number(this.otherAmount);
    const selected = Number(this.selectedAmount);

    const baseAmount = other || selected || 0;
    const fee = this.coverFee ? baseAmount * 0.05 : 0;

    this.totalAmount = baseAmount + fee;
  }

  onOtherAmountChange(value: string) {
    const amount = parseFloat(value);
    if (!isNaN(amount) && amount > 0) {
      this.otherAmount = amount;
      this.selectedAmount = null;
    }
  }

  onAnonymousChange(): void {
    if (this.isAnonymous) {
      this.donorInputValue = '';
      this.selectedDonor = '';
      this.showDonorNameAlert = false;
    } else {
      this.oidcSecurityService.getUserData().subscribe(result => {
        if (result) {
          this.donorInputValue = result.name;
        }
      });
    }
  }

  onBlurUpdateDonor() {

    if (!this.isAnonymous && !this.donorInputValue.trim()) {
      this.showDonorNameAlert = true;
    }
  }
  onInputDonorNameChange(value: string) {

    this.donorInputValue = value;
    if (value?.trim()) {
      this.showDonorNameAlert = false;
      this.selectedDonor = value;
    } else {
      this.selectedDonor = '';
    }
  }

  get coverFeeAmount(): number {
    return this.selectedAmount! * this.coverFeeRate;
  }

  get totalAmountControl(): number {
    return this.selectedAmount! + this.coverFeeAmount;
    // return this.selectedAmount! + this.coverFeeAmount;
  }

  selectAmount(amount: number) {
    this.isValidAmount = true;
    this.selectedAmount = amount;
    this.totalAmount = amount;
    this.otherAmount = null;
  }

  openLoginModal() {

    if (!this.isAnonymous && this.donorInputValue.length === 0) {
      this.showDonorNameAlert = true;
      return;
    }
    const isTestAuction = this.auctionConfigSignal()?.isTestAuction;
    this.authService.openLoginModal(
      LOGIN_TYPE_TICKETS,
      this.embeddedPaymentLoad,
      this.id,
      this.embeddedPaymentLoad,
      '',
      { hideGuestOption: isTestAuction }
    );
  }

  embeddedPaymentLoad = (): void => {

    if (!this.isAnonymous && this.donorInputValue.length === 0) {
      this.showDonorNameAlert = true;
      return;
    }


    const lineItems: ILineItems[] = [
      { id: this.id, type: 1, quantity: 1, donationAmount: this.totalAmount! }
    ];

    if (this.auctionConfigSignal()?.isTestAuction) {
      this.paymentService.postTestPurchase(this.id, lineItems, this.comment ?? '', this.donorInputValue).subscribe({
        next: () => {
          this.router.navigate(['/payment-successful']);
        },
        error: (err) => {
          let errorMsg = 'Unknown error';
          if (typeof err.error === 'string') {
            try {
              const parsed = JSON.parse(err.error);
              errorMsg = parsed.message || errorMsg;
            } catch (e) {
              errorMsg = err.error;
            }
          } else if (err.error && err.error.errorMessage) {
            errorMsg = err.error.errorMessage;
          }
          this.messageService.error(errorMsg || 'Unknown error');
        }
      });
      return;
    }

    this.paymentService.postEmbeddedPayment(this.id, lineItems, this.comment ?? '', this.donorInputValue).subscribe({
      next: (token) => {
        this.openEmbeddedPayment(token);
      },
      error: (err) => {
        let errorMsg = 'Unknown error';

        if (typeof err.error === 'string') {
          try {
            const parsed = JSON.parse(err.error);
            errorMsg = parsed.message || errorMsg;
          } catch (e) {
            errorMsg = err.error;
          }
        } else if (err.error && err.error.errorMessage) {
          errorMsg = err.error.errorMessage;
        }

        this.messageService.error(errorMsg || 'Unknown error');
      }
    });
  }
}


